import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CityDeliveryDiaptchBoardService from "app/data/city-delivery/dispatch-board/cityDeliveryDiaptchBoardService";
import {
  GetDoorsRequest,
  SetupDoorRequest,
  CloseDoorRequest,
  CloseDoorResponse,
  DispatchManifestRequest,
} from "app/data/city-delivery/dispatch-board/models";
import { QUERY_KEYS } from "app/data/common/queryKeys";

const invalidateDispatchBoardQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_DISPATCH_BOARD_QUEUE] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_DISPATCH_BOARD_QUEUE_LOAD] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_DISPATCH_BOARD_QUEUE_LOAD_DETAILS] });
};

export const useGetDoors = (requestData: GetDoorsRequest) => {
  const {
    data: doorsData,
    isLoading: isLoadingDoors,
    refetch: refetchDoors
  } =
    useQuery({
      queryKey: [
        QUERY_KEYS.GET_DISPATCH_BOARD_DOORS,
        requestData.terminalNumber,
        requestData.doorNumberSearch,
        requestData.doorTypes,
      ],
      queryFn: async () => {
        if (requestData && requestData.terminalNumber) {
          const response = await CityDeliveryDiaptchBoardService.getInstance().getDoors(requestData);
          return response;
        }
      },
      refetchOnWindowFocus: false,
      enabled: true,
      retry: true,
    })
  return {
    doorsData,
    isLoadingDoors,
    refetchDoors
  };
}

export const useSetupDoor = (
  onSuccess: () => void,
  onError: (e: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SetupDoorRequest) => {
      const response = await CityDeliveryDiaptchBoardService.getInstance().setupDoor(data);
      if (response?.ok && !response.ok()) {
        throw response;
      }
      return response;
    },
    onSuccess: () => {
      invalidateDispatchBoardQueries(queryClient);
      onSuccess();
    },
    onError: (e: any) => {
      onError(e);
    },
  });
};

export const useCloseDoor = (
  onSuccess: (responseData?: CloseDoorResponse) => void,
  onError: (e: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CloseDoorRequest) => {
      const response = await CityDeliveryDiaptchBoardService.getInstance().closeDoor(data);
      if (response?.ok && !response.ok()) {
        throw response;
      }
      return response?.data;
    },
    onSuccess: (data) => {
      invalidateDispatchBoardQueries(queryClient);
      onSuccess(data);
    },
    onError: (e: any) => {
      onError(e);
    },
  });
};

export const useDispatchManifest = (
  onSuccess: () => void,
  onError: (e: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: DispatchManifestRequest) => {
      const response = await CityDeliveryDiaptchBoardService.getInstance().dispatchManifest(data);
      if (response?.ok && !response.ok()) {
        throw response;
      }
      return response;
    },
    onSuccess: () => {
      invalidateDispatchBoardQueries(queryClient);
      onSuccess();
    },
    onError: (e: any) => {
      onError(e);
    },
  });
};